import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import type { UseFormMethods } from "react-hook-form";
import { isSameDay } from "date-fns";
import { MenuItem } from "@mui/material";
import { IInstallationAppointmentForm } from "./IInstallationAppointmentForm";
import { IAvailableDate } from "./mappers/mapAvailableAppointmentList";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";

interface IParams {
    availableDateList: IAvailableDate[];
    formMethods: UseFormMethods<IInstallationAppointmentForm>;
    fullWidth?: boolean;
}

export const InstallationTimeSlot = (props: IParams) => {
    const { availableDateList, formMethods, fullWidth } = props;
    const { errors, control, watch } = formMethods;
    const { t } = useTranslation(["installation", "validation"]);
    const selectedDate = watch("appointmentDate");

    // Find available slots for selected date
    const availableSlotsForDate = useMemo(() => {
        if (!selectedDate) {
            return [];
        }

        const selectedDateData = availableDateList.find((availableDate) => isSameDay(availableDate.date, selectedDate));

        if (!selectedDateData) {
            return [];
        }

        return selectedDateData.slots.map((slot) => ({
            value: slot,
            label: slot,
        }));
    }, [selectedDate, availableDateList]);

    return (
        <Controller
            control={control}
            name="timeSlot"
            render={(renderProps) => (
                <LabeledTextField
                    className="xtest_installation_time_slot_input"
                    disabled={!selectedDate || availableSlotsForDate.length === 0}
                    error={Boolean(errors.timeSlot)}
                    fullWidth={fullWidth}
                    helperText={errors.timeSlot?.message}
                    id="timeSlot"
                    inputRef={renderProps.ref}
                    isRequired
                    labelText={t("installation:timeSlot")}
                    name={renderProps.name}
                    select
                    value={renderProps.value ?? ""}
                    onChange={renderProps.onChange}
                >
                    {availableSlotsForDate.length === 0 ? (
                        <MenuItem disabled value="">
                            {selectedDate ? t("installation:timeSlot") : ""}
                        </MenuItem>
                    ) : (
                        availableSlotsForDate.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))
                    )}
                </LabeledTextField>
            )}
            rules={{
                required: t("validation:required"),
            }}
        />
    );
};
